import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { unwrap, unwrapNoContent } from "./client";

/**
 * The run's tenant voice: the Liv speech of the tenant the run was recorded on.
 *
 *  - `GET  …/preview-runs/{previewRunRef}/speech/config` — what the tenant offers;
 *  - `POST …/speech/tts` — text in, audio out;
 *  - `POST …/speech/stt` — audio in, text out.
 *
 * Pure pass-through on the service side. Patient text and audio live only in
 * function arguments and local component state: never in a query key, storage
 * or a log line. A failed config read leaves the result `undefined`, which the
 * Lab reads as "use the browser's speech".
 */
export type PreviewSpeechConfig = components["schemas"]["PreviewSpeechConfig"];
export type PreviewSpeechSttResult =
  components["schemas"]["PreviewSpeechSttResult"];

type Client = ReturnType<typeof useApiClient>;

/** The per-tenant config changes with a deployment, not a session. */
const CONFIG_STALE_MS = 30 * 60_000;

export function usePreviewSpeechConfigQuery(
  projectId: string,
  previewRunRef: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<PreviewSpeechConfig> {
  const client = useApiClient();
  return useQuery({
    queryKey: ["projects", projectId, "preview-speech-config", previewRunRef],
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET(
          "/v1/projects/{projectId}/preview-runs/{previewRunRef}/speech/config",
          { params: { path: { projectId, previewRunRef } }, signal },
        ),
      ),
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    staleTime: CONFIG_STALE_MS,
    retry: false,
  });
}

/** Speaks `text` with the tenant's voice; the audio bytes (MP3). Throws `ApiError`. */
export async function synthesizeSpeech(
  client: Client,
  projectId: string,
  previewRunRef: string,
  body: { text: string; language?: "he-IL" | "en-US" },
  signal?: AbortSignal,
): Promise<Blob> {
  const result = await client.POST(
    "/v1/projects/{projectId}/preview-runs/{previewRunRef}/speech/tts",
    {
      params: { path: { projectId, previewRunRef } },
      body,
      parseAs: "blob",
      signal,
    },
  );
  unwrapNoContent(result);
  return result.data as Blob;
}

/**
 * The FormData wire body. The browser adds the multipart boundary, so no
 * Content-Type is set anywhere (a hand-written header would break it).
 */
function sttBody(body: { audio: string; language?: string | null }): FormData {
  const form = new FormData();
  // `format: binary` is typed `string`; the value is the recorded Blob.
  form.append("audio", body.audio as unknown as Blob, "speech.wav");
  if (body.language) {
    form.append("language", body.language);
  }
  return form;
}

/** Transcribes a WAV recording with the tenant's recogniser. Throws `ApiError`. */
export async function transcribeSpeech(
  client: Client,
  projectId: string,
  previewRunRef: string,
  input: { audio: Blob; language: "he-IL" | "en-US" },
  signal?: AbortSignal,
): Promise<PreviewSpeechSttResult> {
  return unwrap(
    await client.POST(
      "/v1/projects/{projectId}/preview-runs/{previewRunRef}/speech/stt",
      {
        params: { path: { projectId, previewRunRef } },
        // `format: binary` is typed `string`; the wire wants the Blob itself.
        body: {
          audio: input.audio as unknown as string,
          language: input.language,
        },
        bodySerializer: sttBody,
        signal,
      },
    ),
  );
}
